// SD25's replacement for SD20's beta transcode-diagnostics panel, and a much smaller thing
// than what it replaces. The old panel existed because the *browser* was running a video
// pipeline whose every give-up point needed to be observable; nothing about that survives
// FR-P3c. What does need explaining is new: a published grackle can legitimately have no quality
// options, because FR-P12's projection decided a packet copy would take too long to do for
// free. This reads `GET /uploads/{id}/publish-status` and says so, with the number.
//
// Dev-mode only, like the panel it replaces — the user-facing version of this conversation is
// the watch page's "Improve quality" action.
import { useEffect, useState } from "react";
import { Card } from "@grackle/ui";
import { api } from "../api/client";
import type { PublishStatus } from "../api/types";

const POLL_MS = 3000;

function remuxLine(status: PublishStatus): string {
  const projected = status.remux_projected_secs;
  const projectedText = projected === null ? "no projection recorded" : `projected ${projected}s`;
  switch (status.remux_state) {
    case "done":
      return `segments produced (${projectedText})`;
    case "deferred":
      // SD57: automatic muxing is an operator setting (off by default), so "deferred" usually
      // just means nobody switched it on — not that this recording was too big.
      return `not muxed automatically — ${projectedText}; muxing is off or on demand, or the recording is over the automatic budget or not packet-copyable`;
    case "failed":
      return `failed — ${status.remux_error ?? "no reason recorded"}`;
    case "not_applicable":
      return "audio only — nothing to segment";
    default:
      return `not decided yet (${projectedText})`;
  }
}

export default function PublishStatusPanel({ grackleId }: { grackleId: string | null }) {
  const [status, setStatus] = useState<PublishStatus | null>(null);

  useEffect(() => {
    if (!grackleId) return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const next = await api<PublishStatus>("GET", `/uploads/${grackleId}/publish-status`);
        if (cancelled) return;
        setStatus(next);
        // Stop polling once the publish job can no longer change anything.
        if (next.grackle_status === "ready" || next.grackle_status === "failed") return;
      } catch {
        // A poll failure is not worth surfacing in a dev panel; the next tick may succeed.
      }
      if (!cancelled) timer = window.setTimeout(poll, POLL_MS);
    };
    void poll();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [grackleId]);

  if (!grackleId || !status) return null;

  return (
    <Card size="sm" className="publish-status-panel">
      <div className="gk-overline">Publish status (dev)</div>
      <dl className="publish-status-rows">
        <dt>Grackle</dt>
        <dd className="mono">{status.grackle_status}</dd>
        <dt>Audio</dt>
        <dd className="mono">
          {status.audio_ready
            ? `extracted${status.audio_bytes ? ` · ${Math.round(status.audio_bytes / 1024)} KB` : ""}`
            : "not yet"}
        </dd>
        <dt>Remux</dt>
        <dd className="mono">{remuxLine(status)}</dd>
        <dt>Renditions</dt>
        <dd className="mono">
          {status.renditions.length === 0
            ? "none"
            : status.renditions.map((r) => r.name).join(", ")}
        </dd>
        {status.transcode_job ? (
          <>
            <dt>Job</dt>
            <dd className="mono">
              {status.transcode_job.status}
              {status.transcode_job.attempts > 1 ? ` · attempt ${status.transcode_job.attempts}` : ""}
              {status.transcode_job.last_error ? ` · ${status.transcode_job.last_error}` : ""}
            </dd>
          </>
        ) : null}
      </dl>
    </Card>
  );
}
